/**
 * Phone helpers used across forms, lead cards, exports and WhatsApp/Call actions.
 *
 * Phone numbers are stored in E.164 format end-to-end (e.g., "+61493800777").
 * Display, tel: links and WhatsApp links all derive from the same E.164 source
 * — no per-country special-cases in UI code.
 */
import {
    isValidPhoneNumber as _isValid,
    parsePhoneNumber as _parsePhoneNumber,
    getCountryCallingCode as _getCountryCallingCode,
} from "libphonenumber-js";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);

/** Returns true iff `value` is a valid E.164 number for its detected country. */
export function isValidPhoneNumber(value) {
    if (!value || typeof value !== "string") return false;
    try {
        return _isValid(value);
    } catch {
        return false;
    }
}

/**
 * Format an E.164 phone for human display.
 * "+61493800777" → "+61 493 800 777"
 * Falls back to the raw value when parsing fails (legacy / pre-migration rows).
 */
export function formatPhoneDisplay(value) {
    if (!value) return "";
    try {
        const p = _parsePhoneNumber(value);
        if (p && p.isValid()) return p.formatInternational();
    } catch {
        /* swallow — fall through to raw */
    }
    return value;
}

/**
 * Build a tel: link from an E.164 number. Spaces and other formatting
 * characters are stripped because tel: handlers prefer compact strings.
 */
export function telLink(value) {
    if (!value) return "";
    return `tel:${value.replace(/[^\d+]/g, "")}`;
}

/**
 * Build a wa.me link. WhatsApp expects digits only — leading '+' must be
 * stripped. We trust the stored E.164 country code and do NOT force-prepend
 * '91' (that was the old India-only behaviour and broke for foreign / NRI leads).
 */
export function whatsappLink(value) {
    if (!value) return "";
    const digits = value.replace(/[^\d]/g, "");
    return `https://wa.me/${digits}`;
}

/**
 * Returns the ISO-2 country (e.g. "IN", "AU") for an E.164 number, or null
 * when the number is invalid or unrecognised.
 */
export function countryOf(value) {
    if (!value) return null;
    try {
        const p = _parsePhoneNumber(value);
        return p?.country || null;
    } catch {
        return null;
    }
}

/**
 * Human-readable country name for an ISO-2 code (e.g. "US" → "United States",
 * "AE" → "United Arab Emirates"). Returns null for unknown codes.
 *
 * Used by the Foreign / NRI demo form to auto-sync the Country combobox with
 * the Phone country picker.
 */
export function countryNameOf(iso2) {
    if (!iso2 || typeof iso2 !== "string") return null;
    return countries.getName(iso2.toUpperCase(), "en", { select: "official" })
        || countries.getName(iso2.toUpperCase(), "en")
        || null;
}

/**
 * Returns the calling-code prefix (e.g. "+1", "+91", "+971") for an ISO-2
 * country. Used to swap the phone widget's flag when the parent picks a
 * different country in the Country combobox.
 */
export function callingCodeOf(iso2) {
    if (!iso2 || typeof iso2 !== "string") return null;
    try {
        const code = _getCountryCallingCode(iso2.toUpperCase());
        return code ? `+${code}` : null;
    } catch {
        return null;
    }
}

/**
 * Reverse lookup — best-effort. Given a free-text country name typed in the
 * Country combobox (e.g. "USA", "United States", "uae"), return the ISO-2
 * code so the Phone widget can change its flag. Returns null when the typed
 * value does not match any known country.
 */
export function iso2OfCountryName(name) {
    if (!name || typeof name !== "string") return null;
    const trimmed = name.trim();
    if (!trimmed) return null;
    // Common short-forms users actually type.
    const ALIASES = {
        "usa": "US", "u.s.a": "US", "u.s.a.": "US", "us": "US", "america": "US",
        "uk": "GB", "u.k.": "GB", "u.k": "GB", "england": "GB", "britain": "GB",
        "great britain": "GB", "united kingdom": "GB",
        "uae": "AE", "u.a.e": "AE", "u.a.e.": "AE",
        "south korea": "KR", "korea": "KR",
        "north korea": "KP",
        "russia": "RU",
        "vietnam": "VN", "viet nam": "VN",
        "ivory coast": "CI",
        "czech republic": "CZ", "czechia": "CZ",
    };
    const aliased = ALIASES[trimmed.toLowerCase()];
    if (aliased) return aliased;
    // Try i18n-iso-countries' built-in name → ISO2 lookup (handles full names).
    const iso = countries.getAlpha2Code(trimmed, "en");
    return iso || null;
}
